// ─────────────────────────────────────────────────────────────────────────────
// Themes — Fig ships four nostalgic-but-distinct palettes. Each theme is a
// flat map of CSS custom properties that override the defaults declared in
// src/index.css. The ThemeProvider applies the chosen theme by writing these
// values onto the document root, and toggles a `data-theme` attribute so a few
// component-level surfaces (paper-bg, ink-bg, swatches) can switch their
// noise/decoration texture per theme.
//
// All colors MUST be HSL triplets ("H S% L%") — they are spliced into
// `hsl(var(--token))` calls throughout the design system. Exception: a token
// may hold a `var(--other-token)` reference when it intentionally re-points to
// another token (e.g. `elevation-block-color` → `var(--safe-area-bg)`).
// ─────────────────────────────────────────────────────────────────────────────

export type ThemeId = "butter" | "velvet-underground" | "kraft" | "scrapbook";

export interface ThemeMeta {
  id: ThemeId;
  name: string;
  tagline: string;
  // Three swatch HSL triplets shown in the menu preview.
  preview: [string, string, string];
  // Texture intensity: drives paper-grain density and decoration density.
  texture: "high" | "medium" | "low";
}

// Token map. Keys are CSS custom property names (without leading `--`).
// Only the Fig palette tokens need to be overridden; the shadcn aliases are
// declared in index.css with `var(--…)` references and follow automatically.
export type ThemeTokens = Record<string, string>;

export const THEMES: Record<ThemeId, ThemeMeta> = {
  butter: {
    id: "butter",
    name: "70s Paperback",
    tagline: "Sun-faded pulp novel — cream pages, rust spine, dusty sky.",
    preview: ["43 59% 90%", "13 62% 45%", "198 24% 63%"],
    texture: "medium",
  },
  "velvet-underground": {
    id: "velvet-underground",
    name: "Velvet Underground",
    tagline: "Foggy ethereal — cool pearl white and silver.",
    preview: ["245 24% 18%", "252 59% 93%", "247 44% 84%"],
    texture: "medium",
  },
  kraft: {
    id: "kraft",
    name: "Library Card",
    tagline: "Card-catalog cream, brass date stamp, forest ink.",
    preview: ["40 55% 85%", "38 76% 39%", "140 33% 14%"],
    texture: "high",
  },
  scrapbook: {
    id: "scrapbook",
    name: "Trapper Keeper",
    tagline: "Y2K fever dream — pastel pink, sequined purple, butterfly clips.",
    preview: ["328 70% 95%", "320 52% 55%", "291 47% 47%"],
    texture: "low",
  },
};

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];
export const DEFAULT_THEME: ThemeId = "butter";

// Each theme overrides the same Fig token set defined as defaults in index.css.
// Comments next to each value give the intended hex for the maintainer.
export const THEME_TOKENS: Record<ThemeId, ThemeTokens> = {
  // ─── Butter zine — the original Fig look. ────────────────────────────────
  butter: {
    // Official FIG palette — 70s Paperback.
    paper: "43 59% 90%",        // #F5EDD8 cream
    "paper-2": "41 53% 85%",    // #EDE0C4 cream dark
    "paper-3": "18 56% 81%",    // #EAC4B4 rust pale
    ink: "28 38% 13%",          // #2C1F14
    "ink-2": "22 31% 27%",      // #5C4030 ink mid
    rust: "13 62% 45%",         // #B94A2C
    "rust-soft": "13 62% 56%",  // #D4694A
    sand: "198 27% 78%",        // #B8CDD6 sky soft
    "sand-deep": "198 24% 38%", // dusty sky deepened for AA
    olive: "22 19% 46%",        // #8C7060 ink light
    cream: "43 59% 95%",        // light cream surface
    sky: "198 24% 63%",         // #8AAAB8 dusty sky
    plum: "28 38% 13%",         // ink
    marigold: "35 64% 53%",     // #D4943A
    "ink-fg": "43 59% 95%",
    "rust-fg": "43 59% 95%",
    "dock-accent": "198 24% 38%", // dusty sky deep — Paperback nav accent
    "dock-accent-fg": "43 59% 95%",
    // safe-area band color — fills the iOS status-bar zone with a dark
    // theme-keyed shade so the gap above the page reads as intentional
    // rather than a flat default.
    "safe-area-bg": "13 70% 22%", // dark burnt orange
  },

  // ─── Velvet Underground — foggy ethereal, cool pearl white and silver. ───
  "velvet-underground": {
    /* Standard Fig tokens — mapped to the foggy ethereal palette */
    paper: "245 24% 18%",       // #242238 deep twilight
    "paper-2": "244 24% 23%",   // #2E2C48 twilight deep
    "paper-3": "244 22% 28%",   // #3A3858 twilight mid
    ink: "252 59% 93%",         // #E8E4F8 pearl white (text)
    "ink-2": "249 38% 85%",     // #D0CCE8 pearl soft
    rust: "247 44% 84%",        // #C8C4E8 lavender accent
    "rust-soft": "249 48% 89%", // #D8D4F0 lavender pale
    sand: "247 25% 71%",        // #A8A4C8 silver
    "sand-deep": "252 22% 64%", // #9890B8 silver deep
    olive: "249 20% 73%",       // #B0ACC8 pearl muted
    cream: "245 24% 18%",       // matches paper — keeps light pearl ink readable
    sky: "247 44% 84%",         // #C8C4E8 lavender accent
    plum: "247 25% 71%",        // #A8A4C8 silver
    marigold: "249 48% 89%",    // #D8D4F0 lavender pale
    "ink-fg": "245 24% 18%",    // twilight on pearl
    "rust-fg": "245 24% 18%",   // twilight on lavender
    "dock-accent": "247 44% 84%", // lavender — Velvet nav accent
    "dock-accent-fg": "245 24% 18%",
    "safe-area-bg": "245 40% 8%", // dark navy / near-black twilight
    // Block-shadow color: velvet's ink is pearl-white, which would glow as a
    // shadow. Reuse the dark safe-area band so block shadows read as depth.
    "elevation-block-color": "var(--safe-area-bg)",

    /* Velvet Underground exact palette tokens */
    "color-paper": "245 24% 18%",
    "color-paper-2": "244 24% 23%",
    "color-paper-3": "244 22% 28%",
    "color-paper-4": "243 21% 34%",
    "color-accent": "247 44% 84%",
    "color-accent-soft": "249 48% 89%",
    "color-accent-text": "245 24% 18%",
    "color-pearl": "252 59% 93%",
    "color-silver": "247 25% 71%",
    "color-ink": "252 59% 93%",
    "color-ink-2": "249 38% 85%",
    "color-ink-light": "249 20% 73%",
    "color-ink-faint": "252 22% 64%",
    "color-nav-active": "252 59% 93%",
    "color-nav-inactive": "252 22% 64%",
    "color-text-secondary": "247 44% 84%",
    "color-text-tertiary": "247 25% 71%",
    "color-text-muted": "252 22% 64%",
    "color-border": "252 60% 90% / 0.12",
    "color-border-strong": "252 60% 90% / 0.20",
    "color-card-bg": "0 0% 100% / 0.07",
    "color-card-border": "252 60% 90% / 0.12",
  },

  // ─── Library Card — cream stock, brass stamp, forest ink. ────────────────
  kraft: {
    /* Standard Fig tokens — mapped to the new Library Card palette */
    paper: "40 55% 85%",        // #EEE0C4 cream paper
    "paper-2": "40 46% 75%",    // #DCC8A0 cream dark
    "paper-3": "40 40% 64%",    // #C8B080 cream deeper
    ink: "26 64% 9%",           // #241408 dark brown ink
    "ink-2": "23 51% 16%",      // #3D2414 ink mid
    rust: "38 76% 39%",         // #B07818 brass
    "rust-soft": "41 62% 56%",  // #D4A848 brass soft
    sand: "142 25% 30%",        // #3A6048 forest pale
    "sand-deep": "150 29% 22%", // #284838 forest soft
    olive: "140 33% 14%",       // #183020 forest
    cream: "40 46% 95%",        // #F8F4EC light cream
    sky: "142 25% 30%",         // #3A6048 forest pale
    plum: "38 76% 39%",         // #B07818 brass accent
    marigold: "43 71% 69%",     // #E8C878 brass pale
    "ink-fg": "40 55% 85%",     // cream on ink
    "rust-fg": "26 64% 9%",     // dark text on brass
    "dock-accent": "140 33% 14%", // forest — Library Card nav accent
    "dock-accent-fg": "40 55% 85%",
    "safe-area-bg": "26 70% 7%", // dark brown

    /* New Library Card tokens (exact user palette) */
    "color-paper": "40 55% 85%",
    "color-paper-2": "40 46% 75%",
    "color-paper-3": "40 40% 64%",
    "color-paper-4": "42 34% 53%",
    "color-forest": "140 33% 14%",
    "color-forest-soft": "150 29% 22%",
    "color-forest-pale": "142 25% 30%",
    "color-forest-text": "40 55% 85%",
    "color-brass": "38 76% 39%",
    "color-brass-soft": "41 62% 56%",
    "color-brass-pale": "43 71% 69%",
    "color-brass-text": "26 64% 9%",
    "color-ink": "26 64% 9%",
    "color-ink-2": "23 51% 16%",
    "color-ink-light": "29 45% 29%",
    "color-ink-faint": "32 32% 46%",
    "color-accent": "140 33% 14%",
    "color-accent-soft": "150 29% 22%",
    "color-accent-text": "40 55% 85%",
    "color-border": "26 64% 9% / 0.14",
    "color-border-strong": "26 64% 9% / 0.22",
    "color-card-bg": "0 0% 100% / 0.45",
    "color-card-border": "26 64% 9% / 0.12",
  },

  // ─── Trapper Keeper — Y2K pink + purple fever dream. ─────────────────────
  // Teal is part of the signature palette and stays in. Two enforced rules:
  //   1. Teal accents (marigold / teal / sky) appear on the pink paper bg,
  //      NEVER as an overlay on a rust/berry surface — would clash visually.
  //   2. Any text rendered on a berry/rust surface is cream (the pink
  //      paper color). The fg tokens (rust-fg, ink-fg, teal-fg,
  //      dock-accent-fg) all resolve to cream so this happens automatically.
  // `olive` is the only AA-fallback that needed a remap — it was berry-mid,
  // which would produce berry-on-berry text on a rust surface. Now plum.
  scrapbook: {
    /* Standard Fig tokens — mapped to the new Trapper Keeper palette */
    paper: "328 70% 95%",       // #FBE8F2 pastel pink paper
    "paper-2": "322 52% 88%",   // #F0D0E4
    "paper-3": "320 40% 82%",   // #E4C0D8
    ink: "335 71% 32%",         // #8C1848 deep berry text — used on pink paper only
    "ink-2": "334 50% 44%",     // #A83868 berry mid
    rust: "320 52% 55%",        // #C850A0 hot pink accent
    "rust-soft": "323 69% 71%", // #E880C0 pink soft
    sand: "290 61% 77%",        // #DCA0E8 purple pale
    "sand-deep": "291 47% 47%", // #A040B0 purple
    // olive stays plum (NOT berry) so any AA-fallback styling that picks
    // olive can sit on a rust surface without producing berry-on-berry.
    olive: "284 60% 28%",       // #4C1B72 deep plum AA-fallback
    cream: "328 70% 97%",       // brightest pink paper — used as fg on every berry surface
    sky: "184 48% 38%",         // #348C8F muted teal — accent for variety on pink paper
    plum: "291 47% 47%",        // #A040B0 purple
    marigold: "186 72% 22%",    // #0F5E63 deep retro teal — the Trapper Keeper accent
    teal: "186 72% 22%",        // #0F5E63 deep retro teal
    "teal-soft": "184 48% 38%", // #348C8F muted teal for chips/borders on pink paper
    // Foreground tokens — ALL resolve to cream (pink paper). Any time a
    // component sets a teal/berry/plum background, the matching `-fg`
    // resolves to cream so text reads cleanly. Never berry-on-berry.
    "teal-fg": "328 70% 97%",   // cream on teal disc
    "ink-fg": "328 70% 97%",    // cream on berry ink
    "rust-fg": "328 70% 97%",   // cream on hot pink rust — prevents berry-on-berry
    "dock-accent": "186 72% 22%", // deep teal nav accent — sits on pink dock, not on rust
    "dock-accent-fg": "328 70% 97%",
    "safe-area-bg": "335 65% 15%", // dark berry

    /* Trapper Keeper exact palette tokens */
    "color-paper": "328 70% 95%",
    "color-paper-2": "322 52% 88%",
    "color-paper-3": "320 40% 82%",
    "color-paper-4": "318 34% 77%",
    "color-pink": "320 52% 55%",
    "color-pink-soft": "323 69% 71%",
    "color-pink-pale": "325 76% 82%",
    "color-pink-text": "328 70% 95%",
    "color-purple": "291 47% 47%",
    "color-purple-soft": "291 53% 61%",
    "color-purple-pale": "290 61% 77%",
    "color-purple-text": "328 70% 95%",
    "color-accent": "320 52% 55%",
    "color-accent-soft": "323 69% 71%",
    "color-accent-text": "328 70% 95%",
    "color-ink": "335 71% 32%",
    "color-ink-2": "334 50% 44%",
    "color-ink-light": "336 34% 53%",
    "color-ink-faint": "343 31% 64%",
    "color-nav-active": "335 71% 32%",
    "color-nav-inactive": "336 34% 53%",
    "color-text-secondary": "334 50% 44%",
    "color-text-tertiary": "336 34% 53%",
    "color-text-muted": "343 31% 64%",
    "color-border": "335 71% 32% / 0.12",
    "color-border-strong": "335 71% 32% / 0.20",
    "color-card-bg": "0 0% 100% / 0.60",
    "color-card-border": "320 52% 55% / 0.15",
    "color-sparkle": "320 52% 55%",
    // Teal palette tokens — kept as deep retro teal. The rule isn't that
    // teal is absent from Trapper Keeper; it's that teal accents sit on
    // pink paper, never overlaid on rust/berry surfaces (those use cream
    // as the foreground via --rust-fg / --ink-fg / --teal-fg).
    "color-teal": "186 72% 22%",
    "color-teal-soft": "184 48% 38%",
  },

};

/**
 * Apply a theme by writing CSS custom properties onto the document root.
 * Also sets `data-theme` so texture utilities (paper-bg, ink-bg, etc.) can
 * branch in CSS.
 */
export function applyTheme(id: ThemeId) {
  if (typeof document === "undefined") return;
  const baseTokens = THEME_TOKENS[id] ?? THEME_TOKENS[DEFAULT_THEME];
  const tokens = enforceContrast(baseTokens);
  const root = document.documentElement;
  for (const [k, v] of Object.entries(tokens)) {
    root.style.setProperty(`--${k}`, v as string);
  }
  root.setAttribute("data-theme", id);
}

// ─── Automatic contrast enforcement ─────────────────────────────────────────
// Some themes (notably "Arcade Cabinet") pair vivid neon foreground colors
// against vivid neon backgrounds. To keep text readable we re-check every
// foreground-ish token against every plausible background-ish token at theme
// apply time, and darken or lighten the foreground until it meets WCAG AA
// (4.5:1) for normal text.

// Foreground tokens are categorised by where they're typically used:
//  - "primary"  : main text colors. Must hit AA against primary paper surfaces.
//  - "accent"   : also used as text on accent surfaces (marigold, sand, etc.).
const PRIMARY_FG_TOKENS = ["ink", "ink-2"] as const;
const ACCENT_FG_TOKENS = ["rust", "sand-deep", "olive", "sky", "plum"] as const;
// Paper-class backgrounds — every FG must be readable on these.
const PAPER_BG_TOKENS = ["paper", "paper-2", "paper-3", "cream"] as const;
// Accent backgrounds — only enforced for accent FGs and the primary "ink"
// (since ink is also stamped on accent chips/buttons in some surfaces).
const ACCENT_BG_TOKENS = ["marigold", "sand"] as const;

// Every surface token that components use as a background. For each one we
// auto-derive a `--{key}-fg` text color that is guaranteed to hit WCAG AA
// (4.5:1) against the surface, picked from the theme's own darkest/lightest
// neutrals — and fall back to pure black/white if neither neutral is enough.
// This is the global "never put same-hue text on same-hue background" rule.
const SURFACE_TOKENS = [
  "paper", "paper-2", "paper-3", "cream",
  "ink", "ink-2",
  "rust", "rust-soft",
  "sand", "sand-deep",
  "olive", "sky", "plum", "marigold",
] as const;

export function parseHsl(triplet: string): [number, number, number] | null {
  const m = triplet.trim().match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%$/);
  if (!m) return null;
  return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])];
}

function formatHsl(h: number, s: number, l: number): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(Math.max(0, Math.min(100, l)))}%`;
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sN = s / 100;
  const lN = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sN * Math.min(lN, 1 - lN);
  const f = (n: number) => lN - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)];
}

export function relLum([r, g, b]: [number, number, number]): number {
  const c = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
}

export function contrastRatio(fg: [number, number, number], bg: [number, number, number]): number {
  const l1 = relLum(fg);
  const l2 = relLum(bg);
  const [a, b] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (a + 0.05) / (b + 0.05);
}

// Smallest angular distance between two hues on the 0..360 wheel.
export function hueDistance(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
}

// Two colors are in the "same color family" when both are reasonably
// saturated AND their hues sit within ~28° on the wheel. Greys and near-greys
// (saturation < 15%) never count as a family match — they're neutral.
export const SAME_FAMILY_HUE_THRESHOLD = 28;
export const NEUTRAL_SAT_THRESHOLD = 15;
export function isSameFamily(
  fg: [number, number, number],
  bg: [number, number, number],
): boolean {
  if (fg[1] < NEUTRAL_SAT_THRESHOLD || bg[1] < NEUTRAL_SAT_THRESHOLD) return false;
  return hueDistance(fg[0], bg[0]) <= SAME_FAMILY_HUE_THRESHOLD;
}

/**
 * Adjust lightness of an HSL color until it has at least `target` contrast
 * against the given background. Tries both directions (darker / lighter) and
 * returns whichever direction reaches the target first; falls back to whichever
 * direction got closer.
 */
/**
 * Adjust lightness of an HSL color away from the background's lightness until
 * it reaches `target` contrast. Direction is forced opposite to the background
 * (dark bg → push fg lighter; light bg → push fg darker). This preserves the
 * overall palette intent (e.g. yellow ink on a dark theme stays light).
 */
function adjustForContrast(
  fg: [number, number, number],
  bg: [number, number, number],
  target = 4.5,
): [number, number, number] {
  const [h, s, l] = fg;
  const bgRgb = hslToRgb(bg[0], bg[1], bg[2]);
  if (contrastRatio(hslToRgb(h, s, l), bgRgb) >= target) return fg;

  const direction = bg[2] >= 50 ? -1 : 1; // light bg → darken fg, dark bg → lighten fg
  const tryL = (nl: number) => contrastRatio(hslToRgb(h, s, nl), bgRgb);

  let bestL = l;
  let bestRatio = tryL(l);
  for (let step = 2; step <= 100; step += 2) {
    const nl = l + direction * step;
    if (nl < 0 || nl > 100) break;
    const r = tryL(nl);
    if (r > bestRatio) {
      bestRatio = r;
      bestL = nl;
    }
    if (r >= target) break;
  }
  return [h, s, bestL];
}

export function enforceContrast(tokens: ThemeTokens): ThemeTokens {
  const out: ThemeTokens = { ...tokens };

  const adjustFg = (fgKey: string, bgKeys: readonly string[]) => {
    const rawFg = out[fgKey];
    const fgHsl = rawFg ? parseHsl(rawFg) : null;
    if (!fgHsl) return;
    // Iterate to a fixed point: each pass adjusts against every bg, then
    // re-checks all bgs, so we don't satisfy the last bg at the cost of
    // the first. Bail when every bg passes or after a few rounds.
    let [h, s, l] = fgHsl;
    const bgs = bgKeys
      .filter((k) => k !== fgKey)
      .map((k) => parseHsl(out[k] ?? ""))
      .filter((b): b is [number, number, number] => b !== null);
    for (let round = 0; round < 5; round++) {
      let allPass = true;
      for (const bg of bgs) {
        const ratio = contrastRatio(hslToRgb(h, s, l), hslToRgb(bg[0], bg[1], bg[2]));
        if (ratio < 4.5) {
          allPass = false;
          [h, s, l] = adjustForContrast([h, s, l], bg, 4.5);
        }
      }
      if (allPass) break;
    }
    out[fgKey] = formatHsl(h, s, l);
  };

  // Primary text colors must be readable on PAPER surfaces only. Accent
  // surfaces (marigold, sand, etc.) are handled by the auto-derived `-fg`
  // tokens below — forcing primary ink to also satisfy accent backgrounds
  // can flip a light ivory ink to dark and break it on dark paper themes.
  for (const fg of PRIMARY_FG_TOKENS) adjustFg(fg, PAPER_BG_TOKENS);
  // Accent FG colors are used as text on paper surfaces; their use as text
  // on accent surfaces is also covered by the `-fg` system.
  for (const fg of ACCENT_FG_TOKENS) adjustFg(fg, PAPER_BG_TOKENS);

  // ─── Auto-derive a contrast-safe `-fg` text color for every surface ─────
  // For each background surface, pick the theme's darkest or lightest neutral
  // (whichever clears 4.5:1) as the text color. If neither hits AA, fall back
  // to pure black or white. Result is written as e.g. `--marigold-fg`, which
  // components can use via `text-marigold-fg` to guarantee readability.
  const lightNeutralKeys = ["cream", "paper", "paper-2"];
  const darkNeutralKeys = ["ink", "ink-2"];

  const tripletByLuminance = (key: string): { triplet: string; lum: number } | null => {
    const raw = out[key];
    const hsl = raw ? parseHsl(raw) : null;
    if (!hsl) return null;
    const [h, s, l] = hsl;
    return { triplet: formatHsl(h, s, l), lum: relLum(hslToRgb(h, s, l)) };
  };

  const lightCandidates = lightNeutralKeys
    .map(tripletByLuminance)
    .filter((c): c is { triplet: string; lum: number } => c !== null)
    .sort((a, b) => b.lum - a.lum); // brightest first
  const darkCandidates = darkNeutralKeys
    .map(tripletByLuminance)
    .filter((c): c is { triplet: string; lum: number } => c !== null)
    .sort((a, b) => a.lum - b.lum); // darkest first

  const PURE_WHITE = "0 0% 100%";
  const PURE_BLACK = "0 0% 0%";

  for (const surfaceKey of SURFACE_TOKENS) {
    const bgRaw = out[surfaceKey];
    const bgHsl = bgRaw ? parseHsl(bgRaw) : null;
    if (!bgHsl) continue;
    const bgRgb = hslToRgb(bgHsl[0], bgHsl[1], bgHsl[2]);
    const bgLum = relLum(bgRgb);

    // Light surfaces prefer dark text first, dark surfaces prefer light text.
    const ordered = bgLum >= 0.5
      ? [...darkCandidates, ...lightCandidates]
      : [...lightCandidates, ...darkCandidates];

    let chosen: string | null = null;
    for (const cand of ordered) {
      const candHsl = parseHsl(cand.triplet)!;
      const ratio = contrastRatio(hslToRgb(candHsl[0], candHsl[1], candHsl[2]), bgRgb);
      if (ratio >= 4.5) { chosen = cand.triplet; break; }
    }
    if (!chosen) {
      // Pick whichever pure extreme has the higher contrast — luminance
      // threshold alone fails for mid-luminance saturated colors like
      // marigold (lum ~0.43 → black wins, not white).
      const blackRatio = contrastRatio(hslToRgb(0, 0, 0), bgRgb);
      const whiteRatio = contrastRatio(hslToRgb(0, 0, 100), bgRgb);
      chosen = blackRatio >= whiteRatio ? PURE_BLACK : PURE_WHITE;
    }
    out[`${surfaceKey}-fg`] = chosen;
  }

  return out;
}

const STORAGE_KEY = "fig:theme";

export function readStoredTheme(): ThemeId {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (THEME_IDS as string[]).includes(raw)) return raw as ThemeId;
  } catch { /* localStorage may be blocked */ }
  return DEFAULT_THEME;
}

export function writeStoredTheme(id: ThemeId) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
}

export function isThemeId(v: unknown): v is ThemeId {
  return typeof v === "string" && (THEME_IDS as string[]).includes(v);
}

// ─── Decoration intensity ───────────────────────────────────────────────────
// A user-level preference (NOT per-theme) that scales the noise/glitter/grid
// overlays each theme paints onto paper-bg / ink-bg. "subtle" keeps the base
// hue but strips most of the texture — better for low-power devices and for
// readers who find the decorations distracting. Stored in localStorage only;
// it's a local accessibility preference, not synced to the user profile.

export type DecorationLevel = "none" | "subtle" | "full";
export const DEFAULT_DECORATION: DecorationLevel = "full";
export const DECORATION_LEVELS: DecorationLevel[] = ["none", "subtle", "full"];
const DECO_STORAGE_KEY = "fig:decoration";

export function applyDecoration(level: DecorationLevel) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-decoration", level);
}

export function readStoredDecoration(): DecorationLevel {
  if (typeof window === "undefined") return DEFAULT_DECORATION;
  try {
    const raw = window.localStorage.getItem(DECO_STORAGE_KEY);
    if (raw === "full" || raw === "subtle" || raw === "none") return raw;
  } catch { /* localStorage may be blocked */ }
  return DEFAULT_DECORATION;
}

export function writeStoredDecoration(level: DecorationLevel) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(DECO_STORAGE_KEY, level); } catch { /* ignore */ }
}

// ─── Theme preferences (per-user customization) ─────────────────────────────
// A bag of overrides on top of the chosen theme. Stored in localStorage and
// synced to profiles.theme_prefs (jsonb) when signed in.
//   - accent: optional HSL triplet that overrides --rust on the document root
//   - paperTexture: how grainy the cream paper background looks

export type PaperTexture = "none" | "subtle" | "full";
export const PAPER_TEXTURES: PaperTexture[] = ["none", "subtle", "full"];

// ─── Font size scale (accessibility) ────────────────────────────────────────
// Multiplies the document root font-size so any rem-based UI scales with it.
// Browsers default to 16px → small=14, normal=16, large=18, xlarge=20.
// The user can override the OS-level browser zoom too; this control is for
// people who don't know browser zoom exists or want app-only scaling.
export type FontScale = "small" | "normal" | "large" | "xlarge";
export const FONT_SCALES: FontScale[] = ["small", "normal", "large", "xlarge"];
// Rebased typography scale (May 2026): the new "normal" baseline pushes body
// text from ~14px to ~16px so labels and forensic copy stay readable in
// thrift-store / market lighting. Each step is +12.5% from the previous.
// Body <zoom> applies these to every hardcoded px size in the app.
export const FONT_SCALE_VALUES: Record<FontScale, number> = {
  small: 1.0,     // previous "normal"
  normal: 1.143,  // ≈ 16/14 — base reading size
  large: 1.286,
  xlarge: 1.429,
};
export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  small: "Small",
  normal: "Normal",
  large: "Large",
  xlarge: "X-Large",
};

function isFontScale(v: unknown): v is FontScale {
  return v === "small" || v === "normal" || v === "large" || v === "xlarge";
}

export function applyFontScale(scale: FontScale) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const mult = FONT_SCALE_VALUES[scale];
  // Two-pronged scaling so both rem-based AND px-based UI grows together:
  //   1. Root font-size handles any rem/em consumers.
  //   2. CSS `zoom` on <body> visually scales everything else (Fig uses
  //      hardcoded px font sizes throughout). zoom is supported in all
  //      modern browsers and preserves layout / hit-testing correctly.
  root.style.fontSize = `${16 * mult}px`;
  if (document.body) {
    (document.body.style as CSSStyleDeclaration & { zoom?: string }).zoom = String(mult);
  }
  root.setAttribute("data-font-scale", scale);
}

// ─── Vision mode (high-contrast accessibility override) ─────────────────────
// Independent of the chosen theme — overlays a pure black/white/grey token
// set on top of any theme. Toggling back to "standard" re-applies the
// underlying theme.
export type VisionMode = "standard" | "high_contrast";
export const VISION_MODES: VisionMode[] = ["standard", "high_contrast"];

function isVisionMode(v: unknown): v is VisionMode {
  return v === "standard" || v === "high_contrast";
}

// ─── Reading style (plain-language toggle) ──────────────────────────────────
export type ReadingStyle = "standard" | "simplified";
export const READING_STYLES: ReadingStyle[] = ["standard", "simplified"];
function isReadingStyle(v: unknown): v is ReadingStyle {
  return v === "standard" || v === "simplified";
}

const HIGH_CONTRAST_TOKENS: ThemeTokens = {
  paper:        "0 0% 100%",
  "paper-2":    "0 0% 96%",
  "paper-3":    "0 0% 92%",
  cream:        "0 0% 100%",
  ink:          "0 0% 0%",
  "ink-2":      "0 0% 10%",
  rust:         "0 0% 0%",
  "rust-soft":  "0 0% 0%",
  sand:         "0 0% 92%",
  "sand-deep":  "0 0% 0%",
  olive:        "0 0% 0%",
  sky:          "0 0% 0%",
  plum:         "0 0% 0%",
  marigold:     "0 0% 0%",
  "ink-fg":        "0 0% 100%",
  "ink-2-fg":      "0 0% 100%",
  "rust-fg":       "0 0% 100%",
  "rust-soft-fg":  "0 0% 100%",
  "sand-fg":       "0 0% 0%",
  "sand-deep-fg":  "0 0% 100%",
  "olive-fg":      "0 0% 100%",
  "sky-fg":        "0 0% 100%",
  "plum-fg":       "0 0% 100%",
  "marigold-fg":   "0 0% 100%",
  "paper-fg":      "0 0% 0%",
  "paper-2-fg":    "0 0% 0%",
  "paper-3-fg":    "0 0% 0%",
  "cream-fg":      "0 0% 0%",
};

export function applyVisionMode(mode: VisionMode, themeId: ThemeId) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode === "high_contrast") {
    for (const [k, v] of Object.entries(HIGH_CONTRAST_TOKENS)) {
      root.style.setProperty(`--${k}`, v);
    }
    root.setAttribute("data-vision", "high-contrast");
  } else {
    root.removeAttribute("data-vision");
    applyTheme(themeId);
  }
}

export interface ThemePrefs {
  /** HSL triplet ("H S% L%") or null to use the theme's default --rust. */
  accent: string | null;
  /** Paper grain intensity, drives `data-decoration` on the document root. */
  paperTexture: PaperTexture;
  /** Root font-size multiplier for accessibility / readability. */
  fontScale: FontScale;
  /** High-contrast accessibility override — forces black/white/grey. */
  visionMode: VisionMode;
  /** Plain-language toggle for verbose copy. */
  readingStyle: ReadingStyle;
}

export const DEFAULT_PREFS: ThemePrefs = {
  accent: null,
  paperTexture: "subtle",
  fontScale: "normal",
  visionMode: "standard",
  readingStyle: "standard",
};

const PREFS_STORAGE_KEY = "fig:theme-prefs";

/** Curated accent palette — works on every theme. HSL triplets. */
export const ACCENT_SWATCHES: { id: string; label: string; hsl: string }[] = [
  { id: "default", label: "Default", hsl: "" }, // empty = clear override
  { id: "coral",    label: "Coral",    hsl: "9 80% 57%" },
  { id: "rust",     label: "Rust",     hsl: "12 70% 45%" },
  { id: "marigold", label: "Marigold", hsl: "38 90% 55%" },
  { id: "olive",    label: "Olive",    hsl: "80 35% 40%" },
  { id: "forest",   label: "Forest",   hsl: "148 50% 30%" },
  { id: "sky",      label: "Sky",      hsl: "200 70% 55%" },
  { id: "indigo",   label: "Indigo",   hsl: "230 60% 50%" },
  { id: "plum",     label: "Plum",     hsl: "290 45% 50%" },
  { id: "magenta",  label: "Magenta",  hsl: "330 80% 55%" },
];

function isPaperTexture(v: unknown): v is PaperTexture {
  return v === "none" || v === "subtle" || v === "full";
}

/**
 * Coerce arbitrary JSON into a safe ThemePrefs, filling defaults.
 *
 * Includes back-compat for the legacy decoration system that lived under
 * the `stickerDensity` key (`none` / `minimal` / `cozy` / `maximalist`).
 * Older stored prefs blobs round-trip into the new `paperTexture` scale
 * so signed-in users don't lose their grain setting on first load.
 */
export function normalizePrefs(raw: unknown): ThemePrefs {
  const out: ThemePrefs = { ...DEFAULT_PREFS };
  if (!raw || typeof raw !== "object") return out;
  const r = raw as Record<string, unknown>;
  if (typeof r.accent === "string" && /^\d+\s+\d+%\s+\d+%$/.test(r.accent.trim())) {
    out.accent = r.accent.trim();
  } else if (r.accent === null) {
    out.accent = null;
  }
  if (isPaperTexture(r.paperTexture)) {
    out.paperTexture = r.paperTexture;
  } else if (typeof r.stickerDensity === "string") {
    // Legacy migration: map the old 4-step density onto the 3-step texture.
    switch (r.stickerDensity) {
      case "none":       out.paperTexture = "none";   break;
      case "minimal":    out.paperTexture = "subtle"; break;
      case "cozy":
      case "maximalist": out.paperTexture = "full";   break;
    }
  }
  if (isFontScale(r.fontScale)) out.fontScale = r.fontScale;
  if (isVisionMode(r.visionMode)) out.visionMode = r.visionMode;
  if (isReadingStyle(r.readingStyle)) out.readingStyle = r.readingStyle;
  return out;
}

export function applyAccent(accent: string | null) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (accent && accent.length > 0) {
    root.style.setProperty("--rust", accent);
  }
  // When accent is null we let the theme's value win — but we already set
  // --rust during applyTheme(), so the theme value is already in place.
}

export function readStoredPrefs(): ThemePrefs {
  if (typeof window === "undefined") return { ...DEFAULT_PREFS };
  try {
    const raw = window.localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return normalizePrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function writeStoredPrefs(prefs: ThemePrefs) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
}

// ─── Theme unlock milestones ────────────────────────────────────────────────
// Themes can be gated behind a posted-scans count. The first two themes are
// always free; the rest unlock as the user posts. Listing happens in the
// ThemeMenu, which calls isThemeUnlocked() for each card.

export interface ThemeUnlock {
  /** Number of posted scans required. 0 = always unlocked. */
  postedScans: number;
  /** Short hint shown on the locked card. */
  hint: string;
}

export const THEME_UNLOCKS: Record<ThemeId, ThemeUnlock> = {
  // All themes are unlocked by default — no posting milestones required.
  butter:    { postedScans: 0, hint: "" },
  kraft:     { postedScans: 0, hint: "" },
  scrapbook: { postedScans: 0, hint: "" },
  "velvet-underground": { postedScans: 0, hint: "" },
};

export function isThemeUnlocked(id: ThemeId, postedScans: number): boolean {
  return postedScans >= THEME_UNLOCKS[id].postedScans;
}
